import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarPlusIcon,
  ChatCircleTextIcon,
  CheckCircleIcon,
  ToothIcon,
  UserIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { SEND_SMS_MUTATION } from 'app/components/contacts/inbox/graphql'
import { ScheduleAppointmentDialog } from 'app/components/scheduling/inapp/ScheduleAppointmentDialog'
import { SendLaterButton } from 'app/components/sms-scheduling/SendLaterButton'
import { Button } from 'app/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'app/components/ui/sheet'
import { useSmsScheduling } from 'app/hooks/useSmsScheduling'
import { gqlFetchOne, gqlMutation } from 'app/lib/graphqlClient'
import { sendAtLabel } from 'app/lib/scheduledSend'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'
import { logError } from 'lib/logging'
import { formatRange } from 'lib/dayClock'
import { PlacedSlot } from './scheduleGrid'
import { SlotCandidate, fitsOpening, openingKind, rankCandidates } from './slotCandidates'
import { Cohort, candidatesIn, cohortsFor } from './slotCohorts'
import { useSlotCandidates } from './useSlotCandidates'

type Props = {
  open: boolean
  date?: string
  slot?: PlacedSlot
  locationId?: string
  onClose: () => void
}

/**
 * What has become of the one text this panel offers.
 *
 * One value rather than four, because they only ever move together, and because
 * a union makes the distinction that matters unrepresentable otherwise: a
 * schedule that failed queued nothing and attempted nothing, so it must never
 * reach the copy that says the text did not reach them.
 */
type OfferOutcome =
  | { kind: `idle` }
  /** `scheduledFor` is set only when the text was queued rather than sent now, so
   *  the confirmation can say when it goes out instead of claiming it already has. */
  | { kind: `sent`; scheduledFor?: string }
  | { kind: `sendFailed` }
  | { kind: `scheduleFailed`; reason: string }

/** Stable identity, so resetting never counts as a change of value. */
const IDLE_OUTCOME: OfferOutcome = { kind: `idle` }

function money(value: number): string {
  return `$${Math.round(value).toLocaleString(`en-US`)}`
}

function firstName(name: string): string {
  return name.split(` `)[0] || name
}

/**
 * What the practice would say, ready to edit.
 *
 * Offered rather than sent: the text is the practice's voice, and a message
 * that goes out without being read by anybody is how a patient gets a time that
 * somebody else took thirty seconds earlier.
 */
function draftFor(candidate: SlotCandidate, date?: string, slot?: PlacedSlot): string {
  const when = date && slot ? `${moment(date).format(`dddd D MMMM`)} at ${formatRange(slot.start, slot.end)}` : `soon`
  return `Hi ${firstName(candidate.name)}, we have had an opening come up on ${when}. Would you like it?`
}

function CohortCard({ cohort, onPick }: { cohort: Cohort; onPick: (label: string) => void }) {
  const isHygiene = cohort.source === `hygiene`
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(cohort.label)}
        className="flex w-full items-center gap-3 rounded-xl border border-default bg-surface-raised px-3 py-3 text-left transition hover:border-strong hover:shadow-elevation-2"
      >
        <span
          className={cn(
            `flex h-8 w-8 shrink-0 items-center justify-center rounded-lg`,
            isHygiene ? `bg-sky-100 text-sky-700` : `bg-amber-100 text-amber-700`,
          )}
        >
          {isHygiene ? <ToothIcon size={16} weight="bold" /> : <UserIcon size={16} weight="bold" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui-sm text-strong">{cohort.label}</span>
          <span className="block text-body-xs text-subtle">
            {cohort.count.toLocaleString(`en-US`)} waiting
            {/* What the practice can act on now, said before they open it: a
                cohort nobody has a mobile number for is a calling list, not a
                texting one. Withheld on a cohort too deep to have been read
                whole, where the figure would describe the sample instead. */}
            {cohort.reachable != null &&
              cohort.reachable < cohort.count &&
              ` · ${cohort.reachable.toLocaleString(`en-US`)} textable`}
          </span>
        </span>
        {/* "Per booking" only when the band really is one number, which the
            hygiene queues are and treatment cases are not. Otherwise the top
            case, which is honest whatever the spread. */}
        {cohort.perBooking ? (
          <span className="shrink-0 text-right">
            <span className="block text-ui-sm tabular-nums text-strong">~{money(cohort.perBooking)}</span>
            <span className="block text-body-xs text-minimal">per booking</span>
          </span>
        ) : cohort.topValue ? (
          <span className="shrink-0 text-right">
            <span className="block text-ui-sm tabular-nums text-strong">{money(cohort.topValue)}</span>
            <span className="block text-body-xs text-minimal">biggest case</span>
          </span>
        ) : null}
        <ArrowRightIcon size={13} weight="bold" className="shrink-0 text-minimal" />
      </button>
    </li>
  )
}

function CandidateRow({ candidate, onPick }: { candidate: SlotCandidate; onPick: (c: SlotCandidate) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(candidate)}
        className="flex w-full items-center gap-3 rounded-lg border border-subtle px-3 py-2.5 text-left transition hover:border-strong hover:bg-surface-subtle"
      >
        <span
          className={cn(
            `flex h-7 w-7 shrink-0 items-center justify-center rounded-full`,
            candidate.source === `hygiene` ? `bg-sky-100 text-sky-700` : `bg-amber-100 text-amber-700`,
          )}
        >
          {candidate.source === `hygiene` ? (
            <ToothIcon size={14} weight="bold" />
          ) : (
            <UserIcon size={14} weight="bold" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui-sm text-strong">{candidate.name}</span>
          {candidate.detail && <span className="block truncate text-body-xs text-subtle">{candidate.detail}</span>}
        </span>
        {candidate.value ? (
          <span className="shrink-0 text-body-sm tabular-nums text-default">{money(candidate.value)}</span>
        ) : null}
        <ArrowRightIcon size={13} weight="bold" className="shrink-0 text-minimal" />
      </button>
    </li>
  )
}

/**
 * Who to put in an opening, one decision at a time.
 *
 * Three steps rather than one list. The practice decides what kind of
 * appointment the chair should take, then which patient, then what to do about
 * them, and each of those is a smaller question than the one before it. Opening
 * straight onto three hundred ranked names asks all three at once.
 *
 * Everything happens here. Texting somebody, or opening the booking, without
 * losing the board behind is the whole point: a front desk that has to leave
 * the calendar to act on it will stop looking at the calendar.
 */
export function SlotCandidatesPanel({ open, date, slot, locationId, onClose }: Props) {
  const kind = useMemo(() => openingKind(slot?.slot.types.map((type) => type.key) ?? []), [slot])
  const pool = useSlotCandidates(locationId, kind)

  const [cohort, setCohort] = useState<string | undefined>()
  const [person, setPerson] = useState<SlotCandidate | undefined>()
  const [draft, setDraft] = useState(``)
  const [sending, setSending] = useState(false)
  const [outcome, setOutcome] = useState<OfferOutcome>(IDLE_OUTCOME)
  const [booking, setBooking] = useState<ContactJson | undefined>()
  const [openingBooking, setOpeningBooking] = useState(false)
  const [bookingFailed, setBookingFailed] = useState(false)

  // The practice clock and the compliance opt-out gate, assembled the same way
  // every other single-recipient composer assembles them.
  const { scheduled, timezone } = useSmsScheduling({
    contactId: person?.contactId,
    phone: person?.phone,
    locationId,
  })

  // A new opening is a new question. Keeping the old cohort selected would open
  // the drawer part way through a decision about a different chair.
  useEffect(() => {
    setCohort(undefined)
    setPerson(undefined)
    setOutcome(IDLE_OUTCOME)
    setBookingFailed(false)
  }, [date, slot?.start])

  const eligible = useMemo(() => pool.candidates.filter((c) => fitsOpening(c, kind)), [pool.candidates, kind])
  const cohorts = useMemo(() => cohortsFor(eligible, pool.totals), [eligible, pool.totals])
  const ranked = useMemo(
    () => (cohort ? rankCandidates(candidatesIn(eligible, cohort), kind) : undefined),
    [eligible, cohort, kind],
  )
  const selected = useMemo(() => cohorts.find((entry) => entry.label === cohort), [cohorts, cohort])
  /**
   * How many the group holds past the shortlist.
   *
   * From the cohort's own count rather than from the rows in hand: the queues
   * are read a hundred deep, so counting what was fetched would tell a practice
   * that a band of two thousand has eighty-eight more people in it.
   */
  const beyondShortlist = selected && ranked ? Math.max(0, selected.count - ranked.fitting.length) : 0

  const pick = useCallback(
    (candidate: SlotCandidate) => {
      setPerson(candidate)
      setDraft(draftFor(candidate, date, slot))
      setOutcome(IDLE_OUTCOME)
    },
    [date, slot],
  )

  const send = useCallback(async () => {
    if (!person?.phone || !locationId || !draft.trim()) return
    setSending(true)
    setOutcome(IDLE_OUTCOME)
    try {
      const result = await gqlMutation(SEND_SMS_MUTATION, {
        input: { to: person.phone, text: draft.trim(), locationId, contactId: person.contactId },
      })
      if (result.error || !result.data?.sendSms?.id) {
        logError(`Schedule could not text this patient about an opening`, result.error)
        setOutcome({ kind: `sendFailed` })
        return
      }
      setOutcome({ kind: `sent` })
    } catch (err) {
      // A rejected request never reaches the `result.error` branch above. Without
      // this the send button stays disabled with nothing said, which reads as a
      // message still going out, and the practice leaves a patient believing they
      // have been offered a time nobody has offered them.
      logError(`Schedule could not text this patient about an opening`, err)
      setOutcome({ kind: `sendFailed` })
    } finally {
      // In `finally` rather than after the call: every path out of here has to
      // give the button back, or the panel is stuck on one failed send.
      setSending(false)
    }
  }, [person, draft, locationId])

  /**
   * Fetch the one person being booked, whole.
   *
   * The list carries six fields a row; the booking dialog reads the full
   * record, including the linked patient ids it has to choose between. Fetching
   * three hundred complete contacts to render twelve names would be most of a
   * megabyte for a panel, so the full read waits until somebody is picked.
   */
  const startBooking = useCallback(async () => {
    if (!person?.contactId) return
    setOpeningBooking(true)
    setBookingFailed(false)
    try {
      const contact = (await gqlFetchOne(`contact`, person.contactId)) as ContactJson | undefined
      if (!contact?.id) throw new Error(`No contact came back`)
      setBooking(contact)
    } catch (err) {
      logError(`Schedule could not open the booking for this candidate`, err)
      setBookingFailed(true)
    } finally {
      setOpeningBooking(false)
    }
  }, [person])

  const when = date && slot ? `${moment(date).format(`dddd D MMMM`)}, ${formatRange(slot.start, slot.end)}` : ``
  const room = slot?.slot.room

  return (
    <>
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-strong">{when}</SheetTitle>
            <p className="text-body-xs text-subtle">{room || `One chair open`}</p>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {(cohort || person) && (
              <button
                type="button"
                onClick={() => (person ? setPerson(undefined) : setCohort(undefined))}
                className="mb-3 inline-flex items-center gap-1.5 text-body-xs text-subtle transition hover:text-strong"
              >
                <ArrowLeftIcon size={12} weight="bold" />
                {person ? cohort : `All groups`}
              </button>
            )}

            {pool.loading && <p className="py-6 text-center text-body-sm text-minimal">Finding who is waiting…</p>}

            {!pool.loading && pool.failed && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-body-xs leading-relaxed text-amber-800">
                <WarningIcon size={13} weight="fill" className="mr-1 inline" />
                The queues did not load, so this panel cannot say who is waiting. It does not mean nobody is.
              </p>
            )}

            {/* Step one: which group. */}
            {!pool.loading && !pool.failed && !cohort && (
              <>
                {cohorts.length === 0 ? (
                  <p className="py-6 text-center text-body-sm text-subtle">Nobody in the queues fits this opening.</p>
                ) : (
                  <>
                    <p className="mb-2 text-body-xs text-subtle">Who could take this chair?</p>
                    <ul className="space-y-2">
                      {cohorts.map((entry) => (
                        <CohortCard key={entry.label} cohort={entry} onPick={setCohort} />
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}

            {/* Step two: which person. */}
            {!pool.loading && cohort && !person && ranked && (
              <>
                <ul className="space-y-1.5">
                  {ranked.fitting.map((candidate) => (
                    <CandidateRow key={candidate.id} candidate={candidate} onPick={pick} />
                  ))}
                </ul>
                <p className="mt-3 text-body-xs leading-relaxed text-minimal">
                  Ranked by what the booking is worth, then by how long they have been waiting.
                  {beyondShortlist > 0 && ` ${beyondShortlist.toLocaleString(`en-US`)} more in this group.`}
                </p>
              </>
            )}

            {/* Step three: what to do about them. */}
            {person && (
              <div className="space-y-4">
                <div>
                  <p className="text-ui-md text-strong">{person.name}</p>
                  {person.detail && <p className="mt-0.5 text-body-xs text-subtle">{person.detail}</p>}
                  {person.value ? (
                    <p className="mt-1 text-body-sm tabular-nums text-default">{money(person.value)} of production</p>
                  ) : null}
                </div>

                {outcome.kind === `sent` ? (
                  <p className="flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-body-sm leading-relaxed text-emerald-800">
                    <CheckCircleIcon size={15} weight="fill" className="mt-0.5 shrink-0" />
                    {outcome.scheduledFor ? `Text scheduled for ${outcome.scheduledFor}.` : `Text sent.`} It lands in
                    their conversation in the inbox, so a reply comes back where the rest of the thread lives.
                  </p>
                ) : person.phone ? (
                  <div>
                    <label className="mb-1 block text-body-xs text-subtle" htmlFor="slot-message">
                      <ChatCircleTextIcon size={13} weight="bold" className="mr-1 inline" />
                      Offer them the time
                    </label>
                    <textarea
                      id="slot-message"
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      rows={4}
                      className="w-full resize-none rounded-lg border border-default px-3 py-2 text-body-sm leading-relaxed text-strong focus:border-strong focus:outline-none"
                    />
                    {outcome.kind === `sendFailed` && (
                      <p className="mt-1 text-body-xs leading-relaxed text-amber-800">
                        <WarningIcon size={12} weight="fill" className="mr-1 inline" />
                        The text did not send, so they have not been told. Nothing was changed.
                      </p>
                    )}
                    {outcome.kind === `scheduleFailed` && (
                      <p className="mt-1 text-body-xs leading-relaxed text-amber-800">
                        <WarningIcon size={12} weight="fill" className="mr-1 inline" />
                        The text was not scheduled, so nothing is queued and they have not been told.{` `}
                        {outcome.reason}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant="inverse"
                        onClick={send}
                        disabled={sending || !draft.trim()}
                        className="flex-1"
                      >
                        {sending ? `Sending…` : `Send text`}
                      </Button>
                      <SendLaterButton
                        timezone={timezone}
                        // `scheduling` as much as an immediate send in flight:
                        // without it a second click while the first schedule is
                        // still going queues this patient a second text.
                        disabled={!draft.trim() || sending || scheduled.scheduling}
                        unavailableReason={scheduled.unavailableReason}
                        onSchedule={async (sendAtLocal) => {
                          const result = await scheduled.schedule(draft.trim(), sendAtLocal)
                          if (result.ok) {
                            setOutcome({
                              kind: `sent`,
                              scheduledFor: timezone ? sendAtLabel(sendAtLocal, timezone) : undefined,
                            })
                          } else {
                            setOutcome({
                              kind: `scheduleFailed`,
                              reason: result.reason ?? `Try picking the time again.`,
                            })
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-subtle">
                    No mobile number on this record, so they cannot be texted. Open them to call instead.
                  </p>
                )}

                {bookingFailed && (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-body-xs leading-relaxed text-amber-800">
                    <WarningIcon size={13} weight="fill" className="mr-1 inline" />
                    That patient&apos;s record did not load, so the booking could not be opened. Nothing was changed.
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  {person.contactId && (
                    <button
                      type="button"
                      onClick={startBooking}
                      disabled={openingBooking}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-default px-3 py-2 text-body-sm text-strong transition hover:border-strong disabled:opacity-50"
                    >
                      <CalendarPlusIcon size={14} weight="bold" />
                      {openingBooking ? `Opening…` : `Book this time`}
                    </button>
                  )}
                  {person.contactId ? (
                    <Link
                      to={`/contacts/${person.contactId}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-default px-3 py-2 text-body-sm text-subtle transition hover:border-strong hover:text-strong"
                    >
                      Open contact
                    </Link>
                  ) : (
                    <Link
                      to="/action-center"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-default px-3 py-2 text-body-sm text-subtle transition hover:border-strong hover:text-strong"
                    >
                      Open case
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {booking && locationId && (
        <ScheduleAppointmentDialog
          contact={booking}
          locationId={locationId}
          open={Boolean(booking)}
          onOpenChange={(next) => !next && setBooking(undefined)}
        />
      )}
    </>
  )
}
